<!--
  SAX Visual Canvas - Konva.js Canvas Component

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/SaxVisualCanvas.vue

  Interactive canvas for editing SAX programs with drag-and-drop, zoom, pan, and link creation
-->

<template>
  <q-card flat bordered class="sax-canvas-wrapper">
    <!-- Canvas Toolbar -->
    <q-toolbar class="bg-grey-2">
      <q-btn-group flat>
        <q-btn
          icon="zoom_in"
          dense
          flat
          :disable="readonly"
          @click="zoomIn"
        >
          <q-tooltip>Zoom In</q-tooltip>
        </q-btn>

        <q-btn
          icon="zoom_out"
          dense
          flat
          :disable="readonly"
          @click="zoomOut"
        >
          <q-tooltip>Zoom Out</q-tooltip>
        </q-btn>

        <q-btn
          icon="fit_screen"
          dense
          flat
          @click="fitToScreen"
        >
          <q-tooltip>Fit to Screen</q-tooltip>
        </q-btn>

        <q-separator vertical />

        <q-btn
          icon="grid_on"
          dense
          flat
          :color="showGrid ? 'primary' : 'grey'"
          @click="toggleGrid"
        >
          <q-tooltip>Toggle Grid</q-tooltip>
        </q-btn>

        <q-btn
          icon="select_all"
          dense
          flat
          :disable="readonly"
          @click="selectAll"
        >
          <q-tooltip>Select All</q-tooltip>
        </q-btn>

        <q-separator vertical />

        <q-btn
          icon="bug_report"
          dense
          flat
          color="orange"
          @click="dumpPathfindingData"
        >
          <q-tooltip>Dump Pathfinding Debug Data</q-tooltip>
        </q-btn>

      </q-btn-group>

      <q-space />

      <!-- Current Folder Indicator -->
      <div v-if="currentFolderId" class="q-mr-md">
        <q-chip
          dense
          icon="folder_open"
          color="amber-7"
          text-color="white"
          size="sm"
        >
          Inside folder
        </q-chip>
      </div>

      <div class="text-caption">
        Zoom: {{ Math.round(currentZoom * 100) }}%
        <span v-if="selectedCount > 0" class="q-ml-sm">
          | Selected: {{ selectedCount }}
        </span>
      </div>
    </q-toolbar>

    <!-- Konva Canvas Container -->
    <div
      ref="canvasWrapper"
      class="sax-canvas-container"
      @drop="handleDrop"
      @dragover.prevent
    >
      <div ref="konvaContainer" class="konva-stage"></div>
    </div>

    <!-- Status Bar -->
    <q-toolbar class="bg-grey-2 text-caption">
      <div v-if="readonly" class="text-grey-7">
        <q-icon name="lock" class="q-mr-xs" />
        Read-only mode
      </div>
      <div v-else-if="linkingSource" class="text-orange-8">
        <q-icon name="info" class="q-mr-xs" />
        Click an input slot to create link
      </div>
      <q-space />
      <div>
        Components: {{ componentCount }} | Links: {{ linkCount }}
      </div>
    </q-toolbar>

    <!-- Context Menu -->
    <q-menu
      v-model="showContextMenu"
      context-menu
      transition-show="jump-down"
      transition-hide="jump-up"
    >
      <!-- Component Context Menu -->
      <q-list v-if="contextMenuType === 'component'" dense style="min-width: 150px">
        <q-item v-close-popup clickable @click="renameComponent">
          <q-item-section avatar>
            <q-icon name="edit" />
          </q-item-section>
          <q-item-section>Rename</q-item-section>
        </q-item>
        <q-item v-close-popup clickable @click="duplicateComponent">
          <q-item-section avatar>
            <q-icon name="content_copy" />
          </q-item-section>
          <q-item-section>Duplicate</q-item-section>
        </q-item>
        <q-separator />
        <q-item v-close-popup clickable class="text-negative" @click="deleteComponent">
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete</q-item-section>
        </q-item>
      </q-list>

      <!-- Link Context Menu -->
      <q-list v-else-if="contextMenuType === 'link'" dense style="min-width: 150px">
        <q-item v-close-popup clickable class="text-negative" @click="deleteLink">
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete Link</q-item-section>
        </q-item>
      </q-list>
    </q-menu>

    <!-- Rename Dialog -->
    <q-dialog v-model="showRenameDialog">
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">Rename Component</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-input
            v-model="renameValue"
            label="Component Name"
            dense
            autofocus
            @keyup.enter="confirmRename"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" color="grey" />
          <q-btn flat label="Rename" color="primary" @click="confirmRename" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Folder Component Selector Dialog -->
    <q-dialog v-model="showFolderComponentSelector" persistent>
      <q-card style="min-width: 450px">
        <q-card-section>
          <div class="text-h6">
            Select Component Inside Folder
          </div>
          <div class="text-caption text-grey-7">
            Folder: {{ folderSelectorData.folderName }}
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none" style="max-height: 400px; overflow-y: auto">
          <q-list bordered separator>
            <q-item
              v-for="comp in folderSelectorData.components"
              :key="comp.id"
              clickable
              :disable="!comp.hasCompatibleSlots"
              @click="selectFolderComponent(comp)"
            >
              <q-item-section avatar>
                <q-icon
                  :name="comp.hasCompatibleSlots ? 'widgets' : 'block'"
                  :color="comp.hasCompatibleSlots ? 'blue-6' : 'grey-4'"
                />
              </q-item-section>

              <q-item-section>
                <q-item-label>{{ comp.name }}</q-item-label>
                <q-item-label caption>{{ comp.type }}</q-item-label>
              </q-item-section>

              <q-item-section v-if="comp.hasCompatibleSlots" side>
                <div class="text-caption text-grey-7">
                  {{ comp.compatibleSlots.length }} compatible slot<span v-if="comp.compatibleSlots.length !== 1">s</span>
                </div>
              </q-item-section>

              <q-item-section v-else side>
                <q-badge color="grey-5" label="Incompatible" />
              </q-item-section>
            </q-item>

            <q-item v-if="folderSelectorData.components.length === 0">
              <q-item-section>
                <q-item-label class="text-grey-6">No components inside this folder</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="grey" @click="cancelFolderComponentSelector" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Folder Slot Selector Dialog (when component has multiple compatible slots) -->
    <q-dialog v-model="showFolderSlotSelector" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">
            Select Slot
          </div>
          <q-item-label caption>
            Component: {{ folderSlotSelectorData.componentName }}
          </q-item-label>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-list bordered separator>
            <q-item
              v-for="slot in folderSlotSelectorData.slots"
              :key="slot.name"
              clickable
              @click="selectFolderSlot(slot)"
            >
              <q-item-section avatar>
                <q-icon name="cable" color="primary" />
              </q-item-section>

              <q-item-section>
                <q-item-label>{{ slot.name }}</q-item-label>
                <q-item-label caption>Type: {{ slot.type || 'any' }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="grey" @click="cancelFolderSlotSelector" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
  import { Notify } from 'quasar';
  import Konva from 'konva';
  import { useSaxEditorStore } from '@/stores/saxEditor';
  import { ComponentNode } from './ComponentNode';
  import { LinkLine } from './LinkLine';
  import { detectCrossings, assignZOrder } from './crossingDetector';

  // ====================================================================
  // PROPS & EMITS
  // ====================================================================

  const props = defineProps({
    equipment: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    siteId: {
      type: Number,
      default: null,
    },
    currentFolderId: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits([
    'component-selected',
    'component-deleted',
    'link-created',
    'link-deleted',
    'modified',
  ]);

  // ====================================================================
  // STORE & STATE
  // ====================================================================

  const saxStore = useSaxEditorStore();

  const canvasWrapper = ref(null);
  const konvaContainer = ref(null);

  // Konva objects
  let stage = null;
  let gridLayer = null;
  let layer = null;
  let transformer = null;

  // Component and link tracking
  const componentNodes = {};
  const linkLines = {};

  // Canvas state
  const showGrid = ref(true);
  const isLinkingMode = ref(false);
  const linkingSource = ref(null);
  const currentZoom = ref(1.0);

  // ====================================================================
  // COMPUTED
  // ====================================================================

  const selectedCount = computed(() => saxStore.selectedComponents.length);
  const componentCount = computed(() => saxStore.componentCount);
  const linkCount = computed(() => saxStore.linkCount);

  // Context menu state
  const showContextMenu = ref(false);
  const contextMenuType = ref(null); // 'component' or 'link'
  const contextMenuTarget = ref(null); // Component ID or Link ID
  const showRenameDialog = ref(false);
  const renameValue = ref('');

  // Folder component selector dialog
  const showFolderComponentSelector = ref(false);
  const folderSelectorData = ref({
    folderId: null,
    folderName: '',
    components: [],
    linkDirection: null, // 'to' or 'from'
  });

  // Folder slot selector dialog (when component has multiple compatible slots)
  const showFolderSlotSelector = ref(false);
  const folderSlotSelectorData = ref({
    componentId: null,
    componentName: '',
    slots: [],
  });

  // ====================================================================
  // LIFECYCLE
  // ====================================================================

  onMounted(async () => {
    await nextTick();
    initializeCanvas();

    // Watch for data changes
    watch(() => saxStore.currentSaxData, rebuildCanvas, { deep: true });
    watch(() => saxStore.selectedComponents, updateSelection, { deep: true });

    // Watch for folder navigation changes
    watch(() => props.currentFolderId, rebuildCanvas);
  });

  onUnmounted(() => {
    if (stage) {
      stage.destroy();
    }
    window.removeEventListener('resize', handleResize);
  });

  // ====================================================================
  // CANVAS INITIALIZATION
  // ====================================================================

  function initializeCanvas() {
    const container = konvaContainer.value;
    const wrapper = canvasWrapper.value;

    if (!container || !wrapper) {return;}

    const width = wrapper.offsetWidth;
    const height = wrapper.offsetHeight || 600;

    // Create stage
    stage = new Konva.Stage({
      container: container,
      width: width,
      height: height,
      draggable: !props.readonly,
    });

    // Grid layer (bottom)
    gridLayer = new Konva.Layer();
    stage.add(gridLayer);
    drawGrid();

    // Main layer (components and links)
    layer = new Konva.Layer();
    stage.add(layer);

    // Transformer for selection
    transformer = new Konva.Transformer({
      enabledAnchors: [],
      rotateEnabled: false,
      borderStroke: '#EF4444',
      borderStrokeWidth: 2,
    });
    layer.add(transformer);

    // Event handlers
    setupEventHandlers();

    // Window resize
    window.addEventListener('resize', handleResize);

    // Initial render
    rebuildCanvas();

    // Apply saved canvas settings
    applyCanvasSettings();
  }

  /**
   * Setup event handlers
   */
  function setupEventHandlers() {
    // Stage click (deselect)
    stage.on('click tap', (e) => {
      if (e.target === stage) {
        saxStore.clearSelection();
        transformer.nodes([]);
        layer.batchDraw();
      }
    });

    // Mouse wheel zoom
    stage.on('wheel', handleWheel);

    // Drag move (update links while dragging for smooth visual feedback)
    stage.on('dragmove', (e) => {
      const targetId = e.target.attrs.id;
      if (targetId && (targetId.startsWith('comp_') || targetId.startsWith('folder_'))) {
        updateLinks();
        updateCrossings();
        layer.batchDraw();
      }
    });

    // Drag end (update component positions)
    stage.on('dragend', (e) => {
      const targetId = e.target.attrs.id;
      if (targetId && (targetId.startsWith('comp_') || targetId.startsWith('folder_'))) {
        const componentId = targetId;
        let pos = e.target.position();

        // ENFORCE POSITIVE COORDINATES - prevent negative positions
        const minX = 0;
        const minY = 0;

        if (pos.x < minX || pos.y < minY) {
          console.warn('[SaxCanvas] Component dragged to negative position, clamping to bounds:', {
            original: pos,
            clamped: { x: Math.max(minX, pos.x), y: Math.max(minY, pos.y) },
          });

          pos = {
            x: Math.max(minX, pos.x),
            y: Math.max(minY, pos.y),
          };

          // Update visual position immediately
          e.target.position(pos);
        }

        // DISABLED: Auto-folder detection when dragging
        // Users should use the tree navigation to move components between folders
        // or use right-click context menu
        //
        // const targetFolder = detectFolderUnderPoint(pos.x + 60, pos.y + 20);
        // if (targetFolder && targetFolder.id !== componentId) {
        //   saxStore.updateComponent(componentId, { parent_id: targetFolder.id });
        //   console.log(`[SaxCanvas] Component ${componentId} moved into folder ${targetFolder.id}`);
        // } else {
        //   saxStore.updateComponent(componentId, { parent_id: null });
        // }

        // Just update position, don't change parent_id
        saxStore.moveComponent(componentId, pos);
        updateLinks();
        emit('modified');
      }
    });
  }

  // ====================================================================
  // FOLDER DETECTION
  // ====================================================================

  /**
   * Detect if a point is inside a folder component
   */
  function detectFolderUnderPoint(x, y) {
    const components = saxStore.currentSaxData?.components || [];

    // Find folders at this position (check from back to front for z-order)
    for (let i = components.length - 1; i >= 0; i--) {
      const comp = components[i];

      // Only check folder types
      if (comp.type !== 'sys::Folder') {continue;}

      const compX = comp.position.x;
      const compY = comp.position.y;
      const compWidth = comp.width || 120;
      const compHeight = comp.height || 100;

      // Check if point is inside folder bounds
      if (x >= compX && x <= compX + compWidth &&
        y >= compY && y <= compY + compHeight) {
        return comp;
      }
    }

    return null;
  }

  // ====================================================================
  // GRID
  // ====================================================================

  function drawGrid() {
    if (!gridLayer) {return;}

    gridLayer.destroyChildren();

    if (!showGrid.value) {
      gridLayer.batchDraw();
      return;
    }

    const width = stage.width();
    const height = stage.height();
    const gridSize = 20; // Grid spacing
    const dotRadius = 1.5; // Small dot radius

    // Draw dot pattern instead of lines for cleaner look
    for (let x = 0; x < width / gridSize; x++) {
      for (let y = 0; y < height / gridSize; y++) {
        gridLayer.add(
          new Konva.Circle({
            x: x * gridSize,
            y: y * gridSize,
            radius: dotRadius,
            fill: '#D1D5DB', // Light gray dots
            opacity: 0.5,
          }),
        );
      }
    }

    gridLayer.batchDraw();
  }

  function toggleGrid() {
    showGrid.value = !showGrid.value;
    drawGrid();
  }

  // ====================================================================
  // CANVAS REBUILD
  // ====================================================================

  function rebuildCanvas() {
    if (!layer || !saxStore.currentSaxData) {return;}

    // Clear existing nodes
    Object.values(componentNodes).forEach((node) => node.destroy());
    Object.values(linkLines).forEach((line) => line.destroy());

    Object.keys(componentNodes).forEach((key) => delete componentNodes[key]);
    Object.keys(linkLines).forEach((key) => delete linkLines[key]);

    // Show only components in the current folder (parent_id matches current folder)
    // null = root level (components with parent_id === null)
    const componentsToRender = saxStore.currentSaxData.components?.filter((component) => {
      return component.parent_id === props.currentFolderId;
    }) || [];

    // Render components in current folder
    componentsToRender.forEach((component) => {
      renderComponent(component);
    });

    // Get IDs of visible components
    const visibleComponentIds = new Set(componentsToRender.map((c) => c.id));

    // Helper: Check if a component is visible OR is inside a visible folder
    const isComponentOrFolderVisible = (componentId) => {
      if (visibleComponentIds.has(componentId)) {return true;}

      // Check if component is inside a visible folder
      const component = saxStore.getComponent(componentId);
      if (component && component.parent_id) {
        return visibleComponentIds.has(component.parent_id);
      }

      return false;
    };

    // Render links sequentially, accumulating routed wire paths as obstacles
    // This prevents the "snake effect" where wires route through offset gaps
    const routedWirePaths = [];

    saxStore.currentSaxData.links?.forEach((link) => {
      const fromVisible = isComponentOrFolderVisible(link.from_component);
      const toVisible = isComponentOrFolderVisible(link.to_component);

      if (fromVisible && toVisible) {
        // Pass existing wire paths to prevent routing through them
        const line = renderLink(link, routedWirePaths);

        // Add this wire's path to the obstacle list for subsequent wires
        if (line && line.getWaypoints) {
          const waypoints = line.getWaypoints();
          if (waypoints && waypoints.length > 0) {
            routedWirePaths.push(waypoints);
          }
        }
      } else {
        // Link not visible, skip rendering
      }
    });

    // Update crossing detection after all links are rendered
    updateCrossings();

    layer.batchDraw();
  }

  /**
   * Render a component node
   */
  function renderComponent(component) {
    const node = new ComponentNode(component, {}, saxStore);
    const group = node.getGroup();

    // Add to layer
    layer.add(group);

    // Store reference
    componentNodes[component.id] = node;

    // Click handler
    group.on('click tap', (e) => {
      e.cancelBubble = true;

      if (isLinkingMode.value) {
        // Handle linking mode click
        handleLinkingClick(component.id, e);
      } else {
        // Normal selection
        const isMultiSelect = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
        saxStore.selectComponent(component.id, isMultiSelect);
        emit('component-selected', component);
      }
    });

    // Right-click context menu
    group.on('contextmenu', (e) => {
      e.cancelBubble = true;
      e.evt.preventDefault();

      if (!props.readonly) {
        handleComponentContextMenu(component.id, e);
      }
    });

    // Slot click handlers - always active for direct linking
    const slots = [...(component.input_slots || []), ...(component.output_slots || [])];
    slots.forEach((slot) => {
      const slotInfo = node.getSlotInfo(slot.name);
      if (slotInfo && slotInfo.circle) {
        slotInfo.circle.on('click tap', (e) => {
          e.cancelBubble = true;
          // Direct linking - no mode required
          handleSlotClick(component.id, slot.name, slotInfo.type);
        });
      }
    });

    return node;
  }

  /**
   * Render a link line
   * @param {Object} link - Link data
   * @param {Array} existingWirePaths - Previously routed wire paths to avoid
   */
  function renderLink(link, existingWirePaths = []) {
    const line = new LinkLine(link, componentNodes, saxStore, {
      existingWirePaths, // Pass existing wires to prevent snake effect
    });
    const group = line.getGroup();

    if (!group) {return;} // Link references missing components

    // Add to layer first, then move to bottom
    layer.add(group);
    group.moveToBottom();

    // Store reference
    linkLines[link.id] = line;

    // Right-click context menu for link
    group.on('contextmenu', (e) => {
      e.cancelBubble = true;
      e.evt.preventDefault();

      if (!props.readonly) {
        showLinkContextMenu(link.id);
      }
    });

    return line;
  }

  /**
   * Update link positions when components move
   */
  function updateLinks() {
    Object.values(linkLines).forEach((line) => {
      line.updatePositions();
    });
  }

  /**
   * Detect and update wire crossings
   */
  function updateCrossings() {
    // Collect waypoints from all links
    const linkWaypoints = {};
    Object.entries(linkLines).forEach(([linkId, linkLine]) => {
      linkWaypoints[linkId] = linkLine.getWaypoints();
    });

    // Detect all crossings
    const crossings = detectCrossings(linkWaypoints);

    // Assign z-order based on link creation order
    const zOrder = assignZOrder(linkWaypoints, crossings);

    // Update bridge rendering for each link
    Object.values(linkLines).forEach((linkLine) => {
      linkLine.updateBridges(crossings, zOrder);
    });
  }

  // ====================================================================
  // LINKING MODE
  // ====================================================================

  function toggleLinkingMode() {
    isLinkingMode.value = !isLinkingMode.value;
    linkingSource.value = null;


    if (isLinkingMode.value) {
      stage.container().style.cursor = 'crosshair';
    } else {
      stage.container().style.cursor = 'default';
    }
  }

  function handleSlotClick(componentId, slotName, slotType) {
    if (!linkingSource.value) {
      // First click - set source (must be output slot)
      if (slotType === 'output') {
        // Check if source is a folder - show folder component selector immediately
        const sourceComponent = saxStore.getComponent(componentId);

        if (sourceComponent && sourceComponent.type === 'sys::Folder') {
          // For linking FROM folder, we set a temporary linking source first
          // so the dialog knows we're in linking mode
          linkingSource.value = {
            componentId,
            slotName,
            dataType: null, // Will be determined when component is selected
          };
          showFolderSelector(componentId, 'from'); // Linking FROM folder
          return;
        }

        // Normal component - get the slot info to store data type
        const sourceNode = componentNodes[componentId];
        const sourceSlotInfo = sourceNode?.getSlotInfo(slotName);

        linkingSource.value = {
          componentId,
          slotName,
          dataType: sourceSlotInfo?.slot?.type, // Store the data type (float, int, bool, etc.)
        };

        // Highlight slot
        if (sourceNode) {
          sourceNode.highlightSlot(slotName, true);
        }

        // Change cursor to indicate linking mode
        stage.container().style.cursor = 'crosshair';
      }
    } else {
      // Second click - create link or cancel
      if (slotType === 'input' && linkingSource.value.componentId !== componentId) {
        // Check if target is a folder - show folder component selector
        const targetComponent = saxStore.getComponent(componentId);

        if (targetComponent && targetComponent.type === 'sys::Folder') {
          showFolderSelector(componentId, 'to'); // Linking TO folder
          return;
        }

        // Normal component-to-component link
        // Check data type compatibility
        const targetNode = componentNodes[componentId];
        const targetSlotInfo = targetNode?.getSlotInfo(slotName);
        const targetDataType = targetSlotInfo?.slot?.type;
        const sourceDataType = linkingSource.value.dataType;

        // Allow linking if:
        // 1. Both slots have no type specified (any), OR
        // 2. Both slots have the same data type
        const typesCompatible = !sourceDataType || !targetDataType || sourceDataType === targetDataType;

        if (!typesCompatible) {
          // Show error notification
          Notify.create({
            type: 'negative',
            message: 'Type Mismatch',
            caption: `Cannot link ${sourceDataType || 'any'} output to ${targetDataType || 'any'} input`,
            position: 'top',
            timeout: 2000,
          });
        } else {
          // Create link from output to input
          const link = {
            id: `link_${Date.now()}`,
            from_component: linkingSource.value.componentId,
            from_slot: linkingSource.value.slotName,
            to_component: componentId,
            to_slot: slotName,
            link_type: 'control',
          };

          saxStore.addLink(link);
          emit('link-created', link);
          emit('modified');

          // Render the new link
          renderLink(link);
          layer.batchDraw();
        }
      }

      // Clear source (cancel or complete)
      if (linkingSource.value) {
        const node = componentNodes[linkingSource.value.componentId];
        if (node) {
          node.highlightSlot(linkingSource.value.slotName, false);
        }
      }

      linkingSource.value = null;
      stage.container().style.cursor = 'default';
    }
  }

  function handleLinkingClick(componentId, event) {
  // Handle component click in linking mode
  // (This is for future enhancement - clicking component body to see slots)
  }

  // ====================================================================
  // FOLDER COMPONENT SELECTOR
  // ====================================================================

  /**
   * Show folder component selector dialog
   * @param {string} folderId - Folder component ID
   * @param {string} linkDirection - 'to' (linking TO folder) or 'from' (linking FROM folder)
   */
  function showFolderSelector(folderId, linkDirection) {
    const folder = saxStore.getComponent(folderId);
    if (!folder || folder.type !== 'sys::Folder') {return;}

    // Get source data type for compatibility filtering
    const sourceDataType = linkingSource.value?.dataType;
    const slotType = linkDirection === 'to' ? 'input' : 'output';

    // Get components inside folder with compatibility info
    const componentsInFolder = saxStore.getComponentsInFolder(folderId);

    const componentList = componentsInFolder.map((comp) => {
      // Find compatible slots based on link direction and data type
      let compatibleSlots = [];

      if (linkDirection === 'to') {
        // Linking TO folder - find compatible input slots
        compatibleSlots = (comp.input_slots || []).filter((slot) => {
          if (!sourceDataType || !slot.type) {return true;} // any type
          return slot.type === sourceDataType || slot.type === 'any' || sourceDataType === 'any';
        });
      } else {
        // Linking FROM folder - find compatible output slots
        compatibleSlots = (comp.output_slots || []).filter((slot) => {
          if (!sourceDataType || !slot.type) {return true;} // any type
          return slot.type === sourceDataType || slot.type === 'any' || sourceDataType === 'any';
        });
      }

      return {
        id: comp.id,
        name: comp.name,
        type: comp.type,
        hasCompatibleSlots: compatibleSlots.length > 0,
        compatibleSlots: compatibleSlots,
      };
    });

    // Sort: compatible first, then by name
    componentList.sort((a, b) => {
      if (a.hasCompatibleSlots !== b.hasCompatibleSlots) {
        return b.hasCompatibleSlots ? 1 : -1;
      }
      return a.name.localeCompare(b.name);
    });

    folderSelectorData.value = {
      folderId: folderId,
      folderName: folder.name,
      components: componentList,
      linkDirection: linkDirection,
    };

    showFolderComponentSelector.value = true;
  }

  /**
   * User selected a component inside folder
   */
  function selectFolderComponent(comp) {
    if (!comp.hasCompatibleSlots) {return;}

    // If only one compatible slot, use it directly
    if (comp.compatibleSlots.length === 1) {
      const slot = comp.compatibleSlots[0];
      createLinkToFolderComponent(comp.id, slot.name);
      showFolderComponentSelector.value = false;
    } else {
      // Multiple compatible slots - show slot selector
      showFolderComponentSelector.value = false;
      showSlotSelector(comp.id, comp.name, comp.compatibleSlots);
    }
  }

  /**
   * Show slot selector when component has multiple compatible slots
   */
  function showSlotSelector(componentId, componentName, slots) {
    folderSlotSelectorData.value = {
      componentId: componentId,
      componentName: componentName,
      slots: slots,
    };

    showFolderSlotSelector.value = true;
  }

  /**
   * User selected a specific slot
   */
  function selectFolderSlot(slot) {
    createLinkToFolderComponent(folderSlotSelectorData.value.componentId, slot.name);
    showFolderSlotSelector.value = false;
  }

  /**
   * Create link to/from component inside folder
   */
  function createLinkToFolderComponent(targetComponentId, targetSlotName) {
    if (!linkingSource.value) {return;}

    const linkDirection = folderSelectorData.value.linkDirection;

    let link;
    if (linkDirection === 'to') {
      // Linking TO folder (source is external, target is inside folder)
      link = {
        id: `link_${Date.now()}`,
        from_component: linkingSource.value.componentId,
        from_slot: linkingSource.value.slotName,
        to_component: targetComponentId,  // Component inside folder
        to_slot: targetSlotName,
        link_type: 'control',
        _metadata: {
          created_via_folder: folderSelectorData.value.folderId,
          created_from_folder_view: props.currentFolderId,
        },
      };
    } else {
      // Linking FROM folder (source is inside folder, target is external)
      link = {
        id: `link_${Date.now()}`,
        from_component: targetComponentId,  // Component inside folder
        from_slot: targetSlotName,
        to_component: linkingSource.value.componentId,
        to_slot: linkingSource.value.slotName,
        link_type: 'control',
        _metadata: {
          created_via_folder: folderSelectorData.value.folderId,
          created_from_folder_view: props.currentFolderId,
        },
      };
    }


    saxStore.addLink(link);
    emit('link-created', link);
    emit('modified');

    // Render the link if both endpoints are visible
    renderLink(link);
    layer.batchDraw();

    // Clear linking state
    if (linkingSource.value) {
      const node = componentNodes[linkingSource.value.componentId];
      if (node) {
        node.highlightSlot(linkingSource.value.slotName, false);
      }
    }

    linkingSource.value = null;
    stage.container().style.cursor = 'default';
  }

  /**
   * Cancel folder component selector
   */
  function cancelFolderComponentSelector() {
    showFolderComponentSelector.value = false;

    // Clear linking state
    if (linkingSource.value) {
      const node = componentNodes[linkingSource.value.componentId];
      if (node) {
        node.highlightSlot(linkingSource.value.slotName, false);
      }
    }

    linkingSource.value = null;
    stage.container().style.cursor = 'default';
  }

  /**
   * Cancel folder slot selector
   */
  function cancelFolderSlotSelector() {
    showFolderSlotSelector.value = false;

    // Clear linking state
    if (linkingSource.value) {
      const node = componentNodes[linkingSource.value.componentId];
      if (node) {
        node.highlightSlot(linkingSource.value.slotName, false);
      }
    }

    linkingSource.value = null;
    stage.container().style.cursor = 'default';
  }

  // ====================================================================
  // CONTEXT MENU
  // ====================================================================

  function handleComponentContextMenu(componentId, event) {
    showComponentContextMenu(componentId);
  }

  // ====================================================================
  // SELECTION
  // ====================================================================

  function updateSelection() {
    // Update visual selection
    Object.keys(componentNodes).forEach((componentId) => {
      const node = componentNodes[componentId];
      const isSelected = saxStore.selectedComponents.includes(componentId);
      node.setSelected(isSelected);
    });

    // Update transformer
    if (saxStore.selectedComponents.length === 1) {
      const componentId = saxStore.selectedComponents[0];
      const node = componentNodes[componentId];
      if (node) {
        transformer.nodes([node.getGroup()]);
      }
    } else {
      transformer.nodes([]);
    }

    layer?.batchDraw();
  }

  function selectAll() {
    saxStore.selectAll();
  }

  // ====================================================================
  // ZOOM & PAN
  // ====================================================================

  function handleWheel(e) {
    e.evt.preventDefault();

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale * 0.9 : oldScale * 1.1;

    // Limit zoom
    const clampedScale = Math.max(0.1, Math.min(3, newScale));

    stage.scale({ x: clampedScale, y: clampedScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    stage.position(newPos);
    stage.batchDraw();

    currentZoom.value = clampedScale;

    // Save canvas settings
    saveCanvasSettings();
  }

  function zoomIn() {
    const oldScale = stage.scaleX();
    const newScale = Math.min(3, oldScale * 1.2);

    stage.scale({ x: newScale, y: newScale });
    stage.batchDraw();

    currentZoom.value = newScale;
    saveCanvasSettings();
  }

  function zoomOut() {
    const oldScale = stage.scaleX();
    const newScale = Math.max(0.1, oldScale * 0.8);

    stage.scale({ x: newScale, y: newScale });
    stage.batchDraw();

    currentZoom.value = newScale;
    saveCanvasSettings();
  }

  function fitToScreen() {
    if (!stage || !saxStore.currentSaxData?.components?.length) {return;}

    // Calculate bounding box of all components
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    saxStore.currentSaxData.components.forEach((comp) => {
      minX = Math.min(minX, comp.position.x);
      minY = Math.min(minY, comp.position.y);
      maxX = Math.max(maxX, comp.position.x + (comp.width || 120));
      maxY = Math.max(maxY, comp.position.y + (comp.height || 80));
    });

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    const scaleX = stage.width() / contentWidth;
    const scaleY = stage.height() / contentHeight;
    const scale = Math.min(scaleX, scaleY, 1) * 0.9; // 90% of max

    stage.scale({ x: scale, y: scale });
    stage.position({
      x: (stage.width() - contentWidth * scale) / 2 - minX * scale,
      y: (stage.height() - contentHeight * scale) / 2 - minY * scale,
    });
    stage.batchDraw();

    currentZoom.value = scale;
    saveCanvasSettings();
  }

  // ====================================================================
  // DRAG & DROP
  // ====================================================================

  function handleDrop(e) {
    e.preventDefault();

    if (props.readonly) {return;}

    const data = e.dataTransfer.getData('application/json');
    if (!data) {return;}

    try {
      const dragData = JSON.parse(data);

      // Unwrap the component data (palette wraps it in { type: 'component-type', data: component })
      const componentType = dragData.data || dragData;

      // Get drop position relative to stage
      const stageBox = stage.container().getBoundingClientRect();
      const scale = stage.scaleX();

      const x = (e.clientX - stageBox.left - stage.x()) / scale;
      const y = (e.clientY - stageBox.top - stage.y()) / scale;

      // Generate unique name by counting existing components of same type
      const existingComponents = saxStore.currentSaxData?.components || [];
      const sameTypeCount = existingComponents.filter((c) => c.type === componentType.name).length;
      const uniqueName = sameTypeCount > 0
        ? `${componentType.name}_${sameTypeCount + 1}`
        : componentType.name;

      // Create component
      const component = {
        id: `comp_${Date.now()}`,
        name: uniqueName,
        type: componentType.name,
        kit: componentType.kit_name,
        parent_id: props.currentFolderId || null, // Assign to current folder
        position: { x, y },
        properties: componentType.default_properties || {},
        input_slots: componentType.input_slots || [],
        output_slots: componentType.output_slots || [],
        width: componentType.width || 120,
        height: componentType.height || 80,
        color: componentType.color || '#3B82F6',
      };

      saxStore.addComponent(component);
      emit('modified');

      // Render the component (only if it's in the current folder)
      if (component.parent_id === props.currentFolderId) {
        renderComponent(component);
        layer.batchDraw();
      }
    } catch (err) {
      console.error('Failed to handle drop:', err);
    }
  }

  // ====================================================================
  // CANVAS SETTINGS
  // ====================================================================

  function saveCanvasSettings() {
    saxStore.updateCanvasSettings({
      zoom: stage.scaleX(),
      pan_x: stage.x(),
      pan_y: stage.y(),
    });
  }

  function applyCanvasSettings() {
    if (!stage || !saxStore.canvasSettings) {return;}

    const settings = saxStore.canvasSettings;

    if (settings.zoom) {
      stage.scale({ x: settings.zoom, y: settings.zoom });
      currentZoom.value = settings.zoom;
    }

    if (settings.pan_x !== undefined && settings.pan_y !== undefined) {
      stage.position({ x: settings.pan_x, y: settings.pan_y });
    }

    stage.batchDraw();
  }

  // ====================================================================
  // RESIZE
  // ====================================================================

  function handleResize() {
    if (!stage || !canvasWrapper.value) {return;}

    const wrapper = canvasWrapper.value;
    stage.width(wrapper.offsetWidth);
    stage.height(wrapper.offsetHeight);

    drawGrid();
    stage.batchDraw();
  }

  // ====================================================================
  // KEYBOARD SHORTCUTS
  // ====================================================================
  // CONTEXT MENU
  // ====================================================================

  function showComponentContextMenu(componentId) {
    contextMenuType.value = 'component';
    contextMenuTarget.value = componentId;
    showContextMenu.value = true;
  }

  function showLinkContextMenu(linkId) {
    contextMenuType.value = 'link';
    contextMenuTarget.value = linkId;
    showContextMenu.value = true;
  }

  function renameComponent() {
    const component = saxStore.currentSaxData.components.find((c) => c.id === contextMenuTarget.value);
    if (component) {
      renameValue.value = component.name;
      showRenameDialog.value = true;
    }
  }

  function confirmRename() {
    const newName = renameValue.value.trim();

    if (!newName) {
      Notify.create({
        type: 'negative',
        message: 'Component name cannot be empty',
        position: 'top',
      });
      return;
    }

    // Check if name already exists (excluding current component)
    const isDuplicate = saxStore.currentSaxData.components.some(
      (c) => c.id !== contextMenuTarget.value && c.name === newName,
    );

    if (isDuplicate) {
      Notify.create({
        type: 'negative',
        message: `Component name "${newName}" already exists`,
        position: 'top',
      });
      return;
    }

    // Update component name
    saxStore.updateComponent(contextMenuTarget.value, { name: newName });
    emit('modified');

    // Update the visual node
    const node = componentNodes[contextMenuTarget.value];
    if (node) {
      node.updateName(newName);
    }

    showRenameDialog.value = false;
  }

  function duplicateComponent() {
    const component = saxStore.currentSaxData.components.find((c) => c.id === contextMenuTarget.value);
    if (component) {
      const newId = `component_${Date.now()}`;
      const offset = { x: 20, y: 20 };

      // Find next available number for duplicate naming (no parentheses - illegal in SAX)
      const baseName = component.name.replace(/_\d+$/, ''); // Remove existing _N suffix
      let copyNumber = 1;
      let newName = `${baseName}_${copyNumber}`;

      // Keep incrementing until we find a unique name
      while (saxStore.currentSaxData.components.some((c) => c.name === newName)) {
        copyNumber++;
        newName = `${baseName}_${copyNumber}`;
      }

      const newComponent = {
        ...component,
        id: newId,
        name: newName,
        position: {
          x: component.position.x + offset.x,
          y: component.position.y + offset.y,
        },
      };

      saxStore.addComponent(newComponent);
      emit('modified');

      // Render the new component
      renderComponent(newComponent);
      layer.batchDraw();
    }
  }

  function deleteComponent() {
    saxStore.deleteComponent(contextMenuTarget.value);
    emit('component-deleted', contextMenuTarget.value);
    emit('modified');

    // Remove from canvas
    const node = componentNodes[contextMenuTarget.value];
    if (node) {
      node.destroy();
      delete componentNodes[contextMenuTarget.value];
    }

    layer.batchDraw();
  }

  function deleteLink() {
    saxStore.deleteLink(contextMenuTarget.value);
    emit('link-deleted', contextMenuTarget.value);
    emit('modified');

    // Remove from canvas
    const linkLine = linkLines[contextMenuTarget.value];
    if (linkLine) {
      linkLine.destroy();
      delete linkLines[contextMenuTarget.value];
    }

    layer.batchDraw();
  }

  // ====================================================================
  // DEBUG PATHFINDING
  // ====================================================================

  function dumpPathfindingData() {

    // Get all components and their bounding boxes
    const componentsData = [];
    Object.entries(componentNodes).forEach(([id, node]) => {
      const comp = node.getComponent();
      const pos = node.getPosition();

      componentsData.push({
        id: comp.id,
        name: comp.name,
        type: comp.type,
        position: pos,
        width: comp.width || 120,
        height: comp.height || 100,
        slots: comp.slots || [],
      });
    });

    // Get all links and their routing
    const linksData = [];
    Object.entries(linkLines).forEach(([id, linkLine]) => {
      const link = saxStore.currentSaxData.links.find((l) => l.id === id);
      if (link) {
        linksData.push({
          id: link.id,
          from: link.from,
          to: link.to,
          waypoints: linkLine.waypoints,
        });
      }
    });

    const debugData = {
      components: componentsData,
      links: linksData,
      canvasSettings: {
        zoom: currentZoom.value,
        showGrid: showGrid.value,
      },
    };


    // Also copy to clipboard if available
    if (navigator.clipboard) {
      navigator.clipboard.writeText(JSON.stringify(debugData, null, 2))
        .then(() => {
          Notify.create({
            type: 'positive',
            message: 'Pathfinding debug data copied to clipboard',
            position: 'top',
          });
        })
        .catch(() => {
          Notify.create({
            type: 'info',
            message: 'Debug data logged to console',
            position: 'top',
          });
        });
    } else {
      Notify.create({
        type: 'info',
        message: 'Debug data logged to console',
        position: 'top',
      });
    }
  }

// ====================================================================

// Note: Keyboard shortcuts handled by parent SaxEditorTab component
</script>

<style scoped lang="scss">
.sax-canvas-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sax-canvas-container {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #F5F5F5; // Slightly darker gray for better contrast with components
  min-height: 0; // Allow flex to control height
}

.konva-stage {
  width: 100%;
  height: 100%;
}
</style>
